import {datadogRum} from '@datadog/browser-rum';
import $ from 'jquery';
import {marked} from 'marked';
import React, {ReactElement} from 'react';
import _ from 'underscore';

import {redirectToSessionExpired} from 'auth/signOut';
import * as benchlingUtil from 'BenchlingUtil';
import {isBrowser} from 'lib/Browser';
import LoggedOutModal, {LOGGED_OUT_MODAL_KEY} from 'nav/LoggedOutModal';
import * as ToastActions from 'nav/toasts/ToastActions';
import {DEFAULT_DURATION, ToastType} from 'nav/toasts/ToastConstants';
import {renderToDOM} from 'redux-stores/ReduxDOMRender';
import {GENERIC_ERROR_MESSAGE, GENERIC_NETWORK_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import * as LegacyModalContainer from 'ui-library/LegacyModalContainer';
import * as logging from 'util/Logging';
import NotificationTemplate from 'util/NotificationTemplate';
import {maybeLogErrorResponseToSentry} from 'util/RequestCorrelation';
import {Optional} from 'util/Types';

// Keep in sync with lib/exceptions.py:GENERIC_FRIENDLY_MSG
const GENERIC_FRIENDLY_MSG = 'Your request could not be completed.';

export const FOREVER_DURATION = 0;

enum SystemErrorType {
  csrf,
}

export interface GenericResponseJSON {
  specError?: never;
  userMessage?: Optional<string>;
  // RFC 7807 problem+json fields returned by the V3 REST API (application/problem+json).
  // `detail` is usually the human-readable message, but V3 also permits an object-valued `detail`
  // (e.g. exception-group errors emit `{error_collection: [...]}`), so consumers must narrow to a
  // string before displaying it. `title` is a short type summary.
  detail?: Optional<string | Record<string, unknown>>;
  title?: Optional<string>;
  devMessage?: Optional<string>;
  systemError?: Optional<SystemErrorJSON>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [k: string]: any;
}

interface SpecErrorResponseJSON {
  specError: true;
  userMessage: string;
  // Present so `detail` can be read off the ResponseJSON union; not emitted for spec errors.
  detail?: Optional<string | Record<string, unknown>>;
  devMessage?: Optional<string>;
  errors: Array<string>;
}

/**
 * For system errors that need special handling, an interface should be created so that the backend payload
 * is well known. This should help with message generation on the frontend.
 */
interface CSRFErrorJSON {
  type: string;
  properties: {
    signinUrl: string;
  };
}
type SystemErrorJSON = CSRFErrorJSON;

export type ResponseJSON = GenericResponseJSON | SpecErrorResponseJSON;

export interface Response {
  isCanceled?: Optional<boolean>;
  status: number;
  userMessage?: Optional<string>;
  // RFC 7807 problem+json message (V3 API). Read off responseJSON in practice, and may be an object
  // (e.g. exception-group `{error_collection: [...]}`), so narrow to a string before displaying.
  detail?: Optional<string | Record<string, unknown>>;
  responseJSON?: ResponseJSON;
  status_url?: string;
  __suppress_notification?: boolean;
}

export function isResponse(o: {status?: number | null}): o is Response {
  return o.status != null;
}

interface Notifications {
  banner(this: void, message: string): void;
  clearBanner(this: void): void;
  close(this: void, id: string): void;
  error(this: void, message: string | ReactElement, options?: NotificationOptions): void;
  getFriendlyError(this: void, res: Response): string | null;
  getFriendlyErrorAsElement(this: void, res: Response, extra: string): JSX.Element | string | null;
  init(): void;
  deinit(): void;
  info(this: void, message: string | ReactElement, options?: NotificationOptions): void;
  notifyError(this: void, res: Response, options?: NotificationOptions): void;
  stickyNotifyForFriendlyError(this: void, res: Response, id: string): void;
  success(this: void, message: string | ReactElement, options?: NotificationOptions): void;
  warn(this: void, message: string | ReactElement, options?: NotificationOptions): void;
}

export enum NotificationType {
  INFO = 'info',
  SUCCESS = 'success',
  DANGER = 'danger',
  WARNING = 'warning',
}

const NOTIFICATION_TYPE_TO_TOAST_TYPE: Record<NotificationType, ToastType> = {
  [NotificationType.INFO]: ToastType.INFO,
  [NotificationType.SUCCESS]: ToastType.SUCCESS,
  [NotificationType.DANGER]: ToastType.DANGER,
  [NotificationType.WARNING]: ToastType.WARNING,
};

export interface NotificationOptions {
  id?: string;
  /** How long to show notification, in seconds. */
  duration?: number;
  closeable?: boolean;
  actionText?: string;
  onActionClick?: (() => void) | (() => Promise<void>);
  onClose?: () => void;
  stack?: boolean;
  extra?: string;
  __suppress_notification?: boolean;
}

let container: JQuery;
let bannerEl: JQuery;
let USE_NEW_TOASTS: number;
const notificationsById: {[s: string]: JQuery} = {};
let _bannerTimeout: number | undefined;
const isBrowserMode = isBrowser; // cache value at file evaluation time

const _notification = (
  type: NotificationType,
  message: string | ReactElement,
  options: NotificationOptions = {}
): void => {
  if (!isBrowserMode) {
    return;
  }

  if ([NotificationType.DANGER, NotificationType.WARNING].includes(type)) {
    datadogRum.addAction(`Notification (${type})`, {id: options.id, type, message});
  }

  if (USE_NEW_TOASTS) {
    const toastOptions = {
      id: options.id,
      type: NOTIFICATION_TYPE_TO_TOAST_TYPE[type],
      message,
      // Use {duration: 0} to make toast never auto-close.
      duration: options.duration,
      isCloseable: options.closeable,
      actionText: options.actionText,
      onActionClick: options.onActionClick,
      onClose: options.onClose,
      stack: options.stack,
    };
    ToastActions.pushToast(toastOptions);
    return;
  }

  // eslint-disable-next-line no-restricted-syntax
  const extendedOptions = _.extend(
    {
      duration: 4,
      closeable: true,
      id: _.uniqueId(),
    },
    options
  );
  const notificationId = extendedOptions.id;
  const notice = $(
    // eslint-disable-next-line new-cap
    NotificationTemplate({
      type,
      message,
      closeable: extendedOptions.closeable,
    })
  );
  if (extendedOptions.onClose != null) {
    $(notice).on('close.bs.alert', extendedOptions.onClose);
  }

  // Clear any notifications that already exist with this id, then reappend it
  notifications.close(extendedOptions.id);
  notificationsById[extendedOptions.id] = notice;
  container.append(notice);
  // Set the timeout for auto-closing this notification
  if (extendedOptions.duration > 0) {
    setTimeout(() => {
      try {
        notifications.close(notificationId);
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (e) {
        logging.error('Failed to close notification', {
          extra: {message: e.message, originalMessage: message},
        });
      }
    }, extendedOptions.duration * 1000);
  }
};

function _handleCSRFError(
  systemError: CSRFErrorJSON,
  isReturnString: boolean,
  extra: string | null = null
): string | JSX.Element {
  if (extra) {
    extra = ` ${extra}`;
  } else {
    extra = '';
  }

  const properties = systemError.properties;
  if (isReturnString) {
    return `Your request could not be completed. Please refresh the page to continue. If you see this message frequently, contact support@benchling.com.${extra}`;
  } else {
    return (
      <>
        Your request could not be completed. Please{' '}
        <a href={properties.signinUrl}>refresh the page to continue</a>. If you see this message frequently,
        contact <a href="mailto:support@benchling.com">support@benchling.com</a>.{extra}
      </>
    );
  }
}
const _handleSpecificSystemErrors: Record<
  SystemErrorType,
  (systemError: SystemErrorJSON, isReturnString: boolean, extra: string | null) => string | JSX.Element
> = {
  [SystemErrorType.csrf]: _handleCSRFError,
};

function _handleSystemError(
  systemError: SystemErrorJSON,
  isReturnString: boolean,
  extra: string | null = null
): string | JSX.Element {
  let defaultMessage = GENERIC_FRIENDLY_MSG;
  if (Object.values(SystemErrorType).includes(systemError.type)) {
    const systemErrorTypeKey = SystemErrorType[systemError.type as keyof typeof SystemErrorType];
    return _handleSpecificSystemErrors[systemErrorTypeKey](systemError, isReturnString, extra);
  }

  if (extra) {
    defaultMessage += ` ${extra}`;
  }
  return defaultMessage;
}

function _getFriendlyErrorWithoutHandlingSystemErrors(res: Response, extra?: string): string | null {
  let message;
  if (res.userMessage != null) {
    message = res.userMessage;
  } else if (res.responseJSON && res.responseJSON.userMessage != null) {
    message = res.responseJSON.userMessage;
  } else if (typeof res.detail === 'string') {
    message = res.detail; // RFC 7807 problem+json (V3 API)
  } else if (res.responseJSON && typeof res.responseJSON.detail === 'string') {
    // Only surface a string `detail`. V3 also permits an object-valued `detail` (e.g. exception-group
    // errors emit `{error_collection: [...]}`), which is not a user-facing message.
    message = res.responseJSON.detail;
  } else {
    return null;
  }
  if (extra) {
    message += `${message.endsWith('.') ? ' ' : '\u2014'}${extra}`;
  }
  return message;
}

const notifications: Notifications = {
  banner(message: string): void {
    if (!isBrowserMode) {
      return;
    }
    clearTimeout(_bannerTimeout);
    bannerEl.html(marked(message)).removeClass('transparent').removeClass('hidden');
    // Set text as title in case it's cut off in HTML.
    bannerEl.attr({title: bannerEl.text().trim()});
  },

  clearBanner(): void {
    if (!isBrowserMode) {
      return;
    }
    bannerEl.addClass('transparent');
    clearTimeout(_bannerTimeout);
    _bannerTimeout = window.setTimeout(() => bannerEl.addClass('hidden'), 200);
  },

  close(identifier: string): void {
    if (!isBrowserMode) {
      return;
    }
    if (USE_NEW_TOASTS) {
      ToastActions.closeToast(identifier);
    } else if (notificationsById[identifier]) {
      notificationsById[identifier].alert('close');
    }
  },

  // eslint-disable-next-line no-restricted-syntax
  error: _.partial(_notification, NotificationType.DANGER),

  getFriendlyError(res: Response): string | null {
    /**
     * getFriendlyError should not be used outside of this class. There are a number of callsites that use it
     * for custom error handling, however, this will be disallowed in the future. As a result of its direct
     * usage, it will not return some conveniences such as rendering URLs as clickable links.
     */
    const responseJSON = res.responseJSON;
    if (responseJSON && 'systemError' in responseJSON && responseJSON.systemError != null) {
      return _handleSystemError(responseJSON.systemError, true) as string;
    } else {
      return _getFriendlyErrorWithoutHandlingSystemErrors(res);
    }
  },

  getFriendlyErrorAsElement(res: Response, extra: string): JSX.Element | string | null {
    /**
     * getFriendlyErrorAsElement is the new way for this class to retrieve the expected message. It will
     * use a React Fragment when appropriate in order to pass back convenience elements such as URLs as clickable
     * links.
     */
    const responseJSON = res.responseJSON;
    if (responseJSON && 'systemError' in responseJSON && responseJSON.systemError != null) {
      return _handleSystemError(responseJSON.systemError, false, extra);
    } else {
      return _getFriendlyErrorWithoutHandlingSystemErrors(res, extra);
    }
  },

  // eslint-disable-next-line no-restricted-syntax
  info: _.partial(_notification, NotificationType.INFO),

  init(): void {
    if (!isBrowserMode) {
      return;
    }
    container = $('.toasts-mount').eq(0);
    USE_NEW_TOASTS = container.length;
    // If we can find a .toasts-mount (i.e., we're in the editor), mount the ToastsContainer.
    // Otherwise fallback to old notifications
    if (USE_NEW_TOASTS) {
      // Inline import to break circular import DataConnector -> ManagedModelDefinitions -> folder-dao ->
      // RequestAccess -> notifications -> ToastsContainer -> DataConnector
      // Likely the DAO should be considered a leaf in our dependency graph, so once that's fixed, this can
      // probably be fixed.
      const ToastsContainer = require('nav/toasts/ToastsContainer').default;
      const toastsContainer = <ToastsContainer />;
      renderToDOM(toastsContainer, container[0]);
    } else {
      container = $('.notifications-container').eq(0);
      if (container.length === 0) {
        container = $('body');
      }
    }
    bannerEl = $('#banner');
  },

  deinit(): void {
    if (!isBrowserMode) {
      return;
    }
    container.empty();
  },

  notifyError(res: Response, options: NotificationOptions = {}): void {
    if (!isBrowserMode) {
      return;
    }
    const {closeable = true, extra = ''} = options;
    let {duration, id} = options;
    // A canceled promise signifies that the calling component is no longer mounted.
    if (res && res.isCanceled) {
      return;
    }
    if (benchlingUtil.shouldSuppressNotification(res, options)) {
      return;
    }
    // If user is logged out, show a modal telling them to login.
    if (res && res.status === 401) {
      if (shouldIdleTimeout(res)) {
        redirectToSessionExpired();
      } else {
        LegacyModalContainer.open(
          LOGGED_OUT_MODAL_KEY,
          <LoggedOutModal
            onRequestClose={() => {
              LegacyModalContainer.close(LOGGED_OUT_MODAL_KEY);
            }}
          />
        );
      }
      return;
    }
    if (res) {
      maybeLogErrorResponseToSentry(res);
    }

    // Keep this logic in sync with benchling.graphql.errors::format_error
    const friendlyMessage = res ? notifications.getFriendlyErrorAsElement(res, extra) : null;
    let message;
    if (friendlyMessage) {
      message = friendlyMessage;
    } else {
      if (res && [502, 503, 504].includes(res.status)) {
        id = '_notifyNetworkErrorDefault';
        message = GENERIC_NETWORK_ERROR_MESSAGE;
      } else if (res && res.status >= 500) {
        id = '_notifyErrorDefault';
        message = GENERIC_ERROR_MESSAGE;
      } else {
        message = GENERIC_FRIENDLY_MSG;
      }
      if (extra) {
        message += ` ${extra}`;
      }
      duration = duration ?? DEFAULT_DURATION;
    }

    if (
      window.debug &&
      res.responseJSON &&
      res.responseJSON.devMessage &&
      res.responseJSON.devMessage !== friendlyMessage
    ) {
      message = (
        <>
          {message} (devMessage: {res.responseJSON.devMessage})
        </>
      );
    }
    notifications.error(message, {duration, id, closeable});
  },

  stickyNotifyForFriendlyError(res: Response, id: string): void {
    if (!isBrowserMode) {
      return;
    }
    const friendlyMessage = notifications.getFriendlyError(res);
    if (friendlyMessage != null) {
      notifications.error(friendlyMessage, {id, duration: 0});
    } else {
      notifications.notifyError(res, {id});
    }
  },

  // eslint-disable-next-line no-restricted-syntax
  success: _.partial(_notification, NotificationType.SUCCESS),

  // eslint-disable-next-line no-restricted-syntax
  warn: _.partial(_notification, NotificationType.WARNING),
};

// TODO: Move definitions of Response and GenericResponse JSON out of this file, and move this function to
// IdleTimeout.ts
export function shouldIdleTimeout(response: Response): boolean {
  const responseJSON = response.responseJSON || response;
  return Boolean((responseJSON as GenericResponseJSON).sessionIdle);
}

export default notifications;
